import { type ChangeEvent, useEffect, useMemo, useState } from 'react'
import {
  BookUser,
  CheckCircle2,
  FileUp,
  Loader2,
  Plus,
  Search,
  Send,
  Smartphone,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/common/Badge'
import { Button } from '@/components/common/Button'
import { Card } from '@/components/common/Card'
import { Input } from '@/components/common/Input'
import { Modal } from '@/components/common/Modal'
import BroadcastsService from '@/services/broadcasts.service'
import ContactsService from '@/services/contacts.service'
import type { Contact } from '@/types'
import type {
  BroadcastCampaign,
  BroadcastCampaignStatus,
  BroadcastCarouselCard,
  BroadcastList,
  BroadcastListContact,
} from '@/types'
import { formatRelativeTime, formatWhatsappDisplayNumber } from '@/utils/formatters'

function renderTemplate(template: string, contact?: BroadcastListContact | null) {
  if (!contact) return template
  const values: Record<string, string> = {
    nome: contact.name ?? '',
    name: contact.name ?? '',
    phone: contact.phone_number ?? '',
    telefone: contact.phone_number ?? '',
    jid: contact.validation_jid ?? contact.jid ?? '',
  }
  Object.entries(contact.custom_fields ?? {}).forEach(([key, value]) => {
    values[key] = String(value ?? '')
  })

  return template.replace(/{{\s*([^}]+)\s*}}/g, (_, key: string) => values[key.trim()] ?? '')
}

function createEmptyCarouselCard(index: number): BroadcastCarouselCard {
  return {
    imageUrl: '',
    title: `Card ${index}`,
    body: '',
    footer: '',
    buttons: [{ id: `auto_generated_${index}_1`, title: 'Comprar' }],
  }
}

export function DispatchTab() {
  const [lists, setLists] = useState<BroadcastList[]>([])
  const [contacts, setContacts] = useState<BroadcastListContact[]>([])
  const [campaigns, setCampaigns] = useState<BroadcastCampaign[]>([])
  const [campaignStatusMap, setCampaignStatusMap] = useState<Record<string, BroadcastCampaignStatus>>({})
  const [selectedListId, setSelectedListId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [isContactLoading, setIsContactLoading] = useState(false)
  const [isSavingList, setIsSavingList] = useState(false)
  const [isSavingContact, setIsSavingContact] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [isSubmittingCampaign, setIsSubmittingCampaign] = useState(false)
  const [newListName, setNewListName] = useState('')
  const [newListDescription, setNewListDescription] = useState('')
  const [manualName, setManualName] = useState('')
  const [manualPhone, setManualPhone] = useState('')
  const [campaignName, setCampaignName] = useState('Campanha de Disparo')
  const [messageMode, setMessageMode] = useState<'text' | 'carousel'>('text')
  const [messageTemplate, setMessageTemplate] = useState('Olá {{nome}}, temos uma oferta para você!')
  const [selectedFileName, setSelectedFileName] = useState<string>('')
  const [carouselTitle, setCarouselTitle] = useState('🛍️ Ofertas Especiais')
  const [carouselBody, setCarouselBody] = useState('Confira nossos produtos!')
  const [carouselFooter, setCarouselFooter] = useState('Loja Virtual')
  const [carouselCards, setCarouselCards] = useState<BroadcastCarouselCard[]>([
    createEmptyCarouselCard(1),
  ])
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [importableContacts, setImportableContacts] = useState<Contact[]>([])
  const [isLoadingImportable, setIsLoadingImportable] = useState(false)
  const [importSearch, setImportSearch] = useState('')
  const [importSelection, setImportSelection] = useState<Set<string>>(new Set())
  const [isImporting, setIsImporting] = useState(false)

  const selectedList = useMemo(
    () => lists.find((list) => list.id === selectedListId) ?? null,
    [lists, selectedListId],
  )

  const previewContact = useMemo(
    () =>
      contacts.find((contact) => contact.validation_status === 'valid') ??
      contacts[0] ??
      null,
    [contacts],
  )

  const previewText = useMemo(
    () => renderTemplate(messageTemplate, previewContact),
    [messageTemplate, previewContact],
  )

  const activeCampaignIds = useMemo(
    () =>
      campaigns
        .filter((campaign) => ['queued', 'running', 'partially_completed'].includes(campaign.status))
        .map((campaign) => campaign.id),
    [campaigns],
  )

  async function refreshCampaignStatuses(campaignIds?: string[]) {
    const ids = campaignIds?.length ? campaignIds : campaigns.slice(0, 10).map((campaign) => campaign.id)
    if (ids.length === 0) return

    const settled = await Promise.allSettled(
      ids.map(async (campaignId) => {
        const status = await BroadcastsService.getCampaignStatus(campaignId)
        return [campaignId, status] as const
      }),
    )

    const nextMap: Record<string, BroadcastCampaignStatus> = {}
    for (const item of settled) {
      if (item.status !== 'fulfilled') continue
      nextMap[item.value[0]] = item.value[1]
    }

    if (Object.keys(nextMap).length === 0) return
    setCampaignStatusMap((current) => ({ ...current, ...nextMap }))
    setCampaigns((current) =>
      current.map((campaign) => (nextMap[campaign.id] ? { ...campaign, ...nextMap[campaign.id] } : campaign)),
    )
  }

  async function loadInitialData() {
    setIsLoading(true)
    try {
      const [listResponse, campaignResponse] = await Promise.all([
        BroadcastsService.listLists({ page: 1, per_page: 50 }),
        BroadcastsService.listCampaigns({ page: 1, per_page: 20 }),
      ])
      setLists(listResponse.data)
      setCampaigns(campaignResponse.data)
      setSelectedListId((current) => current || listResponse.data[0]?.id || '')
      await refreshCampaignStatuses(campaignResponse.data.slice(0, 10).map((campaign) => campaign.id))
    } catch {
      toast.error('Não foi possível carregar o módulo de disparo em massa.')
    } finally {
      setIsLoading(false)
    }
  }

  async function loadContacts(listId: string) {
    if (!listId) {
      setContacts([])
      return
    }
    setIsContactLoading(true)
    try {
      const perPage = 100
      const collected: typeof contacts = []
      let page = 1
      let total = 0
      do {
        const response = await BroadcastsService.listContacts(listId, { page, per_page: perPage })
        collected.push(...response.data)
        total = response.total
        page += 1
      } while (collected.length < total && page <= 50)
      setContacts(collected)
    } catch {
      toast.error('Não foi possível carregar os contatos da lista selecionada.')
    } finally {
      setIsContactLoading(false)
    }
  }

  useEffect(() => {
    loadInitialData().catch(() => undefined)
  }, [])

  useEffect(() => {
    loadContacts(selectedListId).catch(() => undefined)
  }, [selectedListId])

  useEffect(() => {
    if (activeCampaignIds.length === 0) return
    const interval = window.setInterval(() => {
      refreshCampaignStatuses(activeCampaignIds).catch(() => undefined)
    }, 5000)
    return () => window.clearInterval(interval)
  }, [activeCampaignIds])

  async function handleCreateList() {
    if (!newListName.trim()) {
      toast.error('Informe um nome para a lista.')
      return
    }
    setIsSavingList(true)
    try {
      const created = await BroadcastsService.createList({
        name: newListName.trim(),
        description: newListDescription.trim() || null,
      })
      setLists((current) => [created, ...current])
      setSelectedListId(created.id)
      setNewListName('')
      setNewListDescription('')
      toast.success('Lista criada com sucesso.')
    } catch {
      toast.error('Não foi possível criar a lista.')
    } finally {
      setIsSavingList(false)
    }
  }

  async function openImportModal() {
    if (!selectedListId) {
      toast.error('Selecione uma lista antes de importar contatos.')
      return
    }
    setIsImportModalOpen(true)
    setImportSearch('')
    setImportSelection(new Set())
    setIsLoadingImportable(true)
    try {
      const perPage = 100
      const collected: Contact[] = []
      let page = 1
      let total = 0
      do {
        const response = await ContactsService.getAll({ page, per_page: perPage })
        collected.push(...response.data)
        total = response.total
        page += 1
        if (response.data.length === 0) break
      } while (collected.length < total && page <= 50)
      setImportableContacts(collected)
    } catch {
      toast.error('Não foi possível carregar os contatos para importação.')
    } finally {
      setIsLoadingImportable(false)
    }
  }

  function toggleImportContact(contactId: string) {
    setImportSelection((current) => {
      const next = new Set(current)
      if (next.has(contactId)) next.delete(contactId)
      else next.add(contactId)
      return next
    })
  }

  function toggleImportAll(filtered: Contact[]) {
    const allSelected = filtered.every((contact) => importSelection.has(contact.id))
    setImportSelection((current) => {
      const next = new Set(current)
      if (allSelected) {
        for (const contact of filtered) next.delete(contact.id)
      } else {
        for (const contact of filtered) next.add(contact.id)
      }
      return next
    })
  }

  async function handleConfirmImport() {
    if (!selectedListId || importSelection.size === 0) {
      toast.error('Selecione ao menos um contato.')
      return
    }
    setIsImporting(true)
    try {
      const summary = await BroadcastsService.importFromGlobal(
        selectedListId,
        Array.from(importSelection),
      )
      toast.success(
        `${summary.imported} contato(s) importado(s). ${summary.skipped} já estavam na lista.`,
      )
      setIsImportModalOpen(false)
      setImportSelection(new Set())
      await loadInitialData()
      await loadContacts(selectedListId)
    } catch {
      toast.error('Não foi possível importar os contatos.')
    } finally {
      setIsImporting(false)
    }
  }

  async function handleDeleteList(list: BroadcastList) {
    const confirmed = window.confirm(
      `Excluir a lista "${list.name}"? Os contatos da página Contatos permanecem intactos — apenas a lista é removida.`,
    )
    if (!confirmed) return
    try {
      await BroadcastsService.deleteList(list.id)
      setLists((current) => current.filter((item) => item.id !== list.id))
      setSelectedListId((current) => {
        if (current !== list.id) return current
        const remaining = lists.filter((item) => item.id !== list.id)
        return remaining[0]?.id ?? ''
      })
      if (selectedListId === list.id) {
        setContacts([])
      }
      toast.success(`Lista "${list.name}" excluída.`)
    } catch {
      toast.error('Não foi possível excluir a lista.')
    }
  }

  async function handleAddManualContact() {
    if (!selectedListId) {
      toast.error('Selecione uma lista antes de adicionar contatos.')
      return
    }
    if (!manualPhone.trim()) {
      toast.error('Informe o telefone do contato.')
      return
    }
    setIsSavingContact(true)
    try {
      const created = await BroadcastsService.createContact(selectedListId, {
        name: manualName.trim() || null,
        phone_number: manualPhone.trim(),
      })
      setContacts((current) => [...current, created])
      setLists((current) =>
        current.map((list) =>
          list.id === selectedListId
            ? { ...list, total_contacts: list.total_contacts + 1, pending_contacts: list.pending_contacts + 1 }
            : list,
        ),
      )
      setManualName('')
      setManualPhone('')
      toast.success('Contato adicionado à lista.')
    } catch {
      toast.error('Não foi possível adicionar o contato.')
    } finally {
      setIsSavingContact(false)
    }
  }

  async function handleImportCsv(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file || !selectedListId) return
    setSelectedFileName(file.name)
    try {
      const summary = await BroadcastsService.importCsv(selectedListId, file)
      toast.success(`CSV processado: ${summary.created} contato(s) importado(s).`)
      await loadInitialData()
      await loadContacts(selectedListId)
    } catch {
      toast.error('Não foi possível importar o CSV.')
    } finally {
      event.target.value = ''
    }
  }

  async function handleValidateList() {
    if (!selectedListId) {
      toast.error('Selecione uma lista antes de validar.')
      return
    }
    setIsValidating(true)
    try {
      const summary = await BroadcastsService.validateList(selectedListId)
      toast.success(
        `Validação concluída: ${summary.valid} válidos, ${summary.invalid} inválidos e ${summary.errors} com erro.`,
      )
      await loadInitialData()
      await loadContacts(selectedListId)
    } catch {
      toast.error('Não foi possível validar os contatos da lista.')
    } finally {
      setIsValidating(false)
    }
  }

  async function handleCreateCampaign() {
    if (!selectedListId) {
      toast.error('Selecione uma lista para disparar.')
      return
    }
    if (messageMode === 'text' && !messageTemplate.trim()) {
      toast.error('Informe a mensagem dinâmica da campanha.')
      return
    }
    if (messageMode === 'carousel' && carouselCards.some((card) => !card.title.trim() || !card.body.trim())) {
      toast.error('Preencha título e body de todos os cards do carrossel.')
      return
    }

    setIsSubmittingCampaign(true)
    try {
      const created = await BroadcastsService.createCampaign({
        list_id: selectedListId,
        name: campaignName.trim() || 'Campanha de Disparo',
        message_mode: messageMode,
        message_template: messageMode === 'text' ? messageTemplate : null,
        carousel:
          messageMode === 'carousel'
            ? {
                title: carouselTitle,
                body: carouselBody,
                footer: carouselFooter || null,
                cards: carouselCards.map((card, cardIndex) => ({
                  ...card,
                  buttons: card.buttons.map((button, buttonIndex) => ({
                    id: button.id || `auto_generated_${cardIndex + 1}_${buttonIndex + 1}`,
                    title: button.title,
                  })),
                })),
              }
            : null,
        rate_limit_seconds: 0,
        execution_window_minutes: 480,
        average_interval_minutes: 12,
        interval_variation_percent: 30,
        immediate_sends_per_instance: 1,
        start_now: true,
      })
      setCampaigns((current) => [created, ...current])
      await refreshCampaignStatuses([created.id])
      toast.success('Campanha iniciada. As mensagens foram adicionadas à fila de envio.')
    } catch {
      toast.error('Não foi possível criar a campanha.')
    } finally {
      setIsSubmittingCampaign(false)
    }
  }

  async function handleStartExistingCampaign(campaignId: string) {
    try {
      const result = await BroadcastsService.startCampaign(campaignId)
      toast.success(result.message)
      await loadInitialData()
      await refreshCampaignStatuses([campaignId])
    } catch {
      toast.error('Não foi possível reenfileirar a campanha.')
    }
  }

  async function handlePauseCampaign(campaignId: string) {
    try {
      await BroadcastsService.pauseCampaign(campaignId)
      toast.success('Campanha pausada.')
      await refreshCampaignStatuses([campaignId])
    } catch {
      toast.error('Não foi possível pausar a campanha.')
    }
  }

  async function handleResumeCampaign(campaignId: string) {
    try {
      const status = await BroadcastsService.resumeCampaign(campaignId)
      setCampaignStatusMap((current) => ({ ...current, [campaignId]: status }))
      setCampaigns((current) => current.map((campaign) => (campaign.id === campaignId ? { ...campaign, ...status } : campaign)))
      toast.success('Campanha retomada e recolocada na fila.')
    } catch {
      toast.error('Não foi possível retomar a campanha.')
    }
  }

  async function handleCancelCampaign(campaignId: string) {
    try {
      const campaign = await BroadcastsService.cancelCampaign(campaignId)
      setCampaigns((current) => current.map((item) => (item.id === campaignId ? campaign : item)))
      toast.success('Campanha cancelada.')
      await refreshCampaignStatuses([campaignId])
    } catch {
      toast.error('Não foi possível cancelar a campanha.')
    }
  }

  function updateCarouselCard(index: number, patch: Partial<BroadcastCarouselCard>) {
    setCarouselCards((current) =>
      current.map((card, currentIndex) => (currentIndex === index ? { ...card, ...patch } : card)),
    )
  }

  if (isLoading) {
    return (
      <div className="stub-panel">
        <Loader2 size={36} className="animate-spin text-[var(--color-brand)]" />
        <h3>Carregando disparo em massa</h3>
        <p>Montando listas, campanhas e fila operacional.</p>
      </div>
    )
  }

  return (
    <div className="space-y-5 pb-6">
      <div className="grid gap-5 xl:grid-cols-[320px,minmax(0,1fr)]">
        <Card className="space-y-4">
          <div>
            <h3 className="text-base font-semibold text-[var(--text-primary)]">
              Listas de contatos
            </h3>
            <p className="mt-1 text-sm text-[var(--text-muted)]">
              Crie listas, importe CSV e distribua a validação pelas instâncias conectadas.
            </p>
          </div>

          <div className="space-y-3">
            <Input
              label="Nome da lista"
              value={newListName}
              onChange={(event) => setNewListName(event.target.value)}
              placeholder="Leads abril"
            />
            <Input
              label="Descrição"
              value={newListDescription}
              onChange={(event) => setNewListDescription(event.target.value)}
              placeholder="Campanha promocional"
            />
            <Button
              icon={<Plus size={14} />}
              onClick={() => handleCreateList().catch(() => undefined)}
              loading={isSavingList}
              fullWidth
            >
              Nova lista
            </Button>
          </div>

          <div className="space-y-3">
            {lists.map((list) => (
              <div
                key={list.id}
                className={`relative w-full rounded-[18px] border p-4 transition ${
                  list.id === selectedListId
                    ? 'border-[rgba(var(--color-brand-rgb),0.28)] bg-[rgba(var(--color-brand-rgb),0.14)]'
                    : 'border-[var(--border-subtle)] bg-[var(--glass-light)]'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setSelectedListId(list.id)}
                  className="block w-full text-left"
                >
                  <div className="flex items-start justify-between gap-3 pr-8">
                    <div>
                      <p className="text-sm font-semibold text-[var(--text-primary)]">{list.name}</p>
                      <p className="mt-1 text-xs text-[var(--text-muted)]">
                        {list.description || 'Sem descrição'}
                      </p>
                    </div>
                    <Badge variant={list.valid_contacts > 0 ? 'success' : 'default'}>
                      {list.total_contacts}
                    </Badge>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Badge variant="success">{list.valid_contacts} válidos</Badge>
                    <Badge variant="error">{list.invalid_contacts} inválidos</Badge>
                    <Badge variant="info">{list.pending_contacts} pendentes</Badge>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation()
                    handleDeleteList(list).catch(() => undefined)
                  }}
                  className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-[12px] border border-[rgba(239,68,68,0.2)] text-[var(--color-error)] transition hover:bg-[rgba(239,68,68,0.08)]"
                  aria-label={`Excluir lista ${list.name}`}
                  title="Excluir lista"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </Card>

        <div className="space-y-5">
          <Card className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="text-base font-semibold text-[var(--text-primary)]">
                  {selectedList?.name ?? 'Selecione uma lista'}
                </h3>
                <p className="mt-1 text-sm text-[var(--text-muted)]">
                  Contatos adicionados automaticamente ao módulo e preparados para validação distribuída.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <label className="inline-flex">
                  <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleImportCsv} />
                  <span className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border-medium)] px-4 text-sm font-semibold text-[var(--text-primary)] transition hover:bg-[var(--glass-light)]">
                    <FileUp size={14} />
                    Upload CSV
                  </span>
                </label>
                <Button
                  variant="outline"
                  icon={<BookUser size={14} />}
                  onClick={() => openImportModal().catch(() => undefined)}
                  disabled={!selectedListId}
                >
                  Importar contatos
                </Button>
                <Button
                  variant="outline"
                  icon={<CheckCircle2 size={14} />}
                  onClick={() => handleValidateList().catch(() => undefined)}
                  loading={isValidating}
                  disabled={!selectedListId || contacts.length === 0}
                >
                  Validar números
                </Button>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr),320px]">
              <div className="rounded-[22px] border border-[var(--border-subtle)] bg-[rgba(12,14,40,0.22)] p-4">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-[var(--text-primary)]">Contatos da lista</p>
                    <p className="text-xs text-[var(--text-muted)]">
                      {selectedFileName ? `Último CSV: ${selectedFileName}` : 'Importe CSV ou adicione manualmente.'}
                    </p>
                  </div>
                  {isContactLoading ? (
                    <Loader2 size={16} className="animate-spin text-[var(--color-brand)]" />
                  ) : null}
                </div>

                <div className="space-y-3">
                  {contacts.length === 0 ? (
                    <div className="rounded-[18px] border border-dashed border-[var(--border-medium)] px-4 py-8 text-center text-sm text-[var(--text-muted)]">
                      Nenhum contato na lista ainda.
                    </div>
                  ) : (
                    contacts.map((contact) => (
                      <div
                        key={contact.id}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3"
                      >
                        <div>
                          <p className="text-sm font-semibold text-[var(--text-primary)]">
                            {contact.name || 'Sem nome'}
                          </p>
                          <p className="text-xs text-[var(--text-muted)]">
                            {formatWhatsappDisplayNumber(contact.validation_jid || contact.jid || contact.phone_number)}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Badge
                            variant={
                              contact.validation_status === 'valid'
                                ? 'success'
                                : contact.validation_status === 'invalid'
                                  ? 'error'
                                  : contact.validation_status === 'error'
                                    ? 'warning'
                                    : 'default'
                            }
                          >
                            {contact.validation_status}
                          </Badge>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="space-y-3 rounded-[22px] border border-[var(--border-subtle)] bg-[rgba(12,14,40,0.22)] p-4">
                <p className="text-sm font-semibold text-[var(--text-primary)]">Inserção manual</p>
                <Input
                  label="Nome"
                  value={manualName}
                  onChange={(event) => setManualName(event.target.value)}
                  placeholder="Joel Quintana"
                />
                <Input
                  label="Telefone"
                  value={manualPhone}
                  onChange={(event) => setManualPhone(event.target.value)}
                  placeholder="55992466329"
                />
                <Button
                  icon={<Plus size={14} />}
                  onClick={() => handleAddManualContact().catch(() => undefined)}
                  loading={isSavingContact}
                  fullWidth
                  disabled={!selectedListId}
                >
                  Adicionar contato
                </Button>
              </div>
            </div>
          </Card>

          <div className="grid gap-5 2xl:grid-cols-[minmax(0,1fr),360px]">
            <Card className="space-y-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="text-base font-semibold text-[var(--text-primary)]">
                    Composição da campanha
                  </h3>
                  <p className="mt-1 text-sm text-[var(--text-muted)]">
                    Envio sequencial entre instâncias conectadas com variáveis dinâmicas e rate limit por instância.
                  </p>
                </div>
                <div className="flex gap-2 rounded-[16px] border border-[var(--border-subtle)] bg-[var(--glass-light)] p-1">
                  <button
                    type="button"
                    onClick={() => setMessageMode('text')}
                    className={`rounded-[12px] px-3 py-2 text-sm font-semibold ${messageMode === 'text' ? 'bg-[rgba(var(--color-brand-rgb),0.16)] text-[var(--color-brand)]' : 'text-[var(--text-secondary)]'}`}
                  >
                    Texto
                  </button>
                  <button
                    type="button"
                    onClick={() => setMessageMode('carousel')}
                    className={`rounded-[12px] px-3 py-2 text-sm font-semibold ${messageMode === 'carousel' ? 'bg-[rgba(var(--color-brand-rgb),0.16)] text-[var(--color-brand)]' : 'text-[var(--text-secondary)]'}`}
                  >
                    Carrossel
                  </button>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label="Nome da campanha"
                  value={campaignName}
                  onChange={(event) => setCampaignName(event.target.value)}
                />
                <div className="flex flex-col justify-end">
                  <p className="rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-3 text-xs text-[var(--text-muted)]">
                    A cadência entre envios é controlada pelo
                    <strong className="mx-1 text-[var(--text-primary)]">Buffer Humanizado</strong>
                    em Monitoramento › Buffer Humanizado.
                  </p>
                </div>
              </div>

              {messageMode === 'text' ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[var(--text-primary)]">Mensagem dinâmica</label>
                  <textarea
                    className="min-h-[180px] w-full rounded-[18px] border border-[var(--border-medium)] bg-[var(--glass-light)] px-4 py-3 text-sm text-[var(--text-primary)] outline-none transition focus:border-[var(--color-brand)] focus:ring-2 focus:ring-[rgba(var(--color-brand-rgb),0.15)]"
                    value={messageTemplate}
                    onChange={(event) => setMessageTemplate(event.target.value)}
                    placeholder="Olá {{nome}}, temos uma oferta para você!"
                  />
                  <p className="text-xs text-[var(--text-muted)]">
                    Variáveis disponíveis: `{'{{nome}}'}`, `{'{{phone}}'}` e colunas extras importadas via CSV.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-3">
                    <Input label="Título" value={carouselTitle} onChange={(event) => setCarouselTitle(event.target.value)} />
                    <Input label="Body" value={carouselBody} onChange={(event) => setCarouselBody(event.target.value)} />
                    <Input label="Footer" value={carouselFooter} onChange={(event) => setCarouselFooter(event.target.value)} />
                  </div>

                  <div className="space-y-4">
                    {carouselCards.map((card, index) => (
                      <div key={`${index}-${card.title}`} className="rounded-[20px] border border-[var(--border-subtle)] bg-[rgba(255,255,255,0.03)] p-4">
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <p className="text-sm font-semibold text-[var(--text-primary)]">Card {index + 1}</p>
                          {carouselCards.length > 1 ? (
                            <button
                              type="button"
                              onClick={() => setCarouselCards((current) => current.filter((_, currentIndex) => currentIndex !== index))}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-[12px] border border-[rgba(239,68,68,0.18)] text-[var(--color-error)]"
                            >
                              <X size={14} />
                            </button>
                          ) : null}
                        </div>
                        <div className="grid gap-3 md:grid-cols-2">
                          <Input
                            label="Image URL"
                            value={card.imageUrl}
                            onChange={(event) => updateCarouselCard(index, { imageUrl: event.target.value })}
                          />
                          <Input
                            label="Título"
                            value={card.title}
                            onChange={(event) => updateCarouselCard(index, { title: event.target.value })}
                          />
                          <Input
                            label="Body"
                            value={card.body}
                            onChange={(event) => updateCarouselCard(index, { body: event.target.value })}
                          />
                          <Input
                            label="Footer"
                            value={card.footer ?? ''}
                            onChange={(event) => updateCarouselCard(index, { footer: event.target.value })}
                          />
                        </div>

                        <div className="mt-4 space-y-2">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--text-muted)]">
                            Botões
                          </p>
                          {card.buttons.map((button, buttonIndex) => (
                            <div key={`${index}-${buttonIndex}`} className="flex gap-2">
                              <Input
                                value={button.title}
                                onChange={(event) =>
                                  setCarouselCards((current) =>
                                    current.map((currentCard, currentIndex) =>
                                      currentIndex === index
                                        ? {
                                            ...currentCard,
                                            buttons: currentCard.buttons.map((currentButton, currentButtonIndex) =>
                                              currentButtonIndex === buttonIndex
                                                ? { ...currentButton, title: event.target.value }
                                                : currentButton,
                                            ),
                                          }
                                        : currentCard,
                                    ),
                                  )
                                }
                                placeholder="Comprar"
                              />
                              {card.buttons.length > 1 ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setCarouselCards((current) =>
                                      current.map((currentCard, currentIndex) =>
                                        currentIndex === index
                                          ? {
                                              ...currentCard,
                                              buttons: currentCard.buttons.filter((_, currentButtonIndex) => currentButtonIndex !== buttonIndex),
                                            }
                                          : currentCard,
                                      ),
                                    )
                                  }
                                  className="inline-flex h-10 w-10 items-center justify-center rounded-[14px] border border-[rgba(239,68,68,0.18)] text-[var(--color-error)]"
                                >
                                  <X size={14} />
                                </button>
                              ) : null}
                            </div>
                          ))}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setCarouselCards((current) =>
                                current.map((currentCard, currentIndex) =>
                                  currentIndex === index
                                    ? {
                                        ...currentCard,
                                        buttons: [
                                          ...currentCard.buttons,
                                          {
                                            id: `auto_generated_${index + 1}_${currentCard.buttons.length + 1}`,
                                            title: 'Novo botão',
                                          },
                                        ],
                                      }
                                    : currentCard,
                                ),
                              )
                            }
                          >
                            Adicionar botão
                          </Button>
                        </div>
                      </div>
                    ))}

                    <Button
                      variant="outline"
                      icon={<Plus size={14} />}
                      onClick={() => setCarouselCards((current) => [...current, createEmptyCarouselCard(current.length + 1)])}
                    >
                      Adicionar card
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  icon={<Send size={14} />}
                  onClick={() => handleCreateCampaign().catch(() => undefined)}
                  loading={isSubmittingCampaign}
                  disabled={!selectedList || selectedList.valid_contacts === 0}
                >
                  Iniciar disparo
                </Button>
              </div>
            </Card>

            <Card className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-[16px] bg-[rgba(var(--color-brand-rgb),0.14)] text-[var(--color-brand)]">
                  <Smartphone size={18} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[var(--text-primary)]">Preview da mensagem</h3>
                  <p className="text-xs text-[var(--text-muted)]">Atualização em tempo real.</p>
                </div>
              </div>

              <div className="mx-auto w-full max-w-[320px] rounded-[30px] border border-[rgba(255,255,255,0.08)] bg-[linear-gradient(180deg,rgba(17,19,42,0.98),rgba(12,14,34,0.96))] p-4 shadow-[0_24px_60px_rgba(7,9,30,0.34)]">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-white">{previewContact?.name || 'Contato de preview'}</p>
                    <p className="text-[11px] text-[rgba(255,255,255,0.55)]">
                      {formatWhatsappDisplayNumber(previewContact?.validation_jid || previewContact?.jid || '55992466329')}
                    </p>
                  </div>
                  <Sparkles size={16} className="text-[var(--color-brand)]" />
                </div>

                {messageMode === 'text' ? (
                  <div className="bubble-outbound ml-auto max-w-[85%] px-4 py-3 text-sm">
                    {previewText || 'A mensagem aparecerá aqui.'}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="rounded-[18px] bg-[rgba(255,255,255,0.06)] p-4 text-sm text-white">
                      <p className="font-semibold">{renderTemplate(carouselTitle, previewContact)}</p>
                      <p className="mt-1 text-[rgba(255,255,255,0.8)]">{renderTemplate(carouselBody, previewContact)}</p>
                      {carouselFooter ? (
                        <p className="mt-2 text-[11px] uppercase tracking-[0.16em] text-[rgba(255,255,255,0.45)]">
                          {renderTemplate(carouselFooter, previewContact)}
                        </p>
                      ) : null}
                    </div>
                    {carouselCards.map((card, index) => (
                      <div key={`${card.title}-${index}`} className="rounded-[20px] border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.05)] p-3 text-sm text-white">
                        <div className="mb-3 aspect-[16/9] rounded-[16px] bg-[rgba(255,255,255,0.08)]" style={card.imageUrl ? { backgroundImage: `url(${renderTemplate(card.imageUrl, previewContact)})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined} />
                        <p className="font-semibold">{renderTemplate(card.title, previewContact)}</p>
                        <p className="mt-1 text-[rgba(255,255,255,0.78)]">{renderTemplate(card.body, previewContact)}</p>
                        {card.footer ? (
                          <p className="mt-2 text-[11px] uppercase tracking-[0.16em] text-[rgba(255,255,255,0.46)]">
                            {renderTemplate(card.footer, previewContact)}
                          </p>
                        ) : null}
                        <div className="mt-3 flex flex-wrap gap-2">
                          {card.buttons.map((button) => (
                            <span
                              key={button.id || button.title}
                              className="inline-flex min-h-[28px] items-center rounded-[14px] border border-[rgba(var(--color-brand-rgb),0.24)] bg-[rgba(var(--color-brand-rgb),0.18)] px-3 text-[11px] font-semibold text-[var(--color-brand)]"
                            >
                              {renderTemplate(button.title, previewContact)}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>

          <Card className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-base font-semibold text-[var(--text-primary)]">Campanhas recentes</h3>
                <p className="mt-1 text-sm text-[var(--text-muted)]">
                  Histórico operacional com status da fila de envio.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {campaigns.length === 0 ? (
                <div className="rounded-[18px] border border-dashed border-[var(--border-medium)] px-4 py-8 text-center text-sm text-[var(--text-muted)]">
                  Nenhuma campanha criada ainda.
                </div>
              ) : (
                campaigns.map((campaign) => {
                  const status = campaignStatusMap[campaign.id] ?? campaign
                  const canRestart = ['draft', 'failed', 'completed', 'completed_with_errors', 'cancelled'].includes(status.status)
                  const canPause = ['queued', 'running', 'partially_completed'].includes(status.status)
                  const canResume = status.status === 'paused'
                  return (
                    <div
                      key={campaign.id}
                      className="rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] px-4 py-4"
                    >
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-semibold text-[var(--text-primary)]">{status.name}</p>
                            <Badge
                              variant={
                                status.status === 'completed'
                                  ? 'success'
                                  : status.status === 'completed_with_errors'
                                    ? 'warning'
                                    : status.status === 'failed' || status.status === 'cancelled'
                                      ? 'error'
                                      : 'info'
                              }
                            >
                              {status.status}
                            </Badge>
                            <Badge variant="brand">{status.message_mode}</Badge>
                          </div>
                          <p className="mt-1 text-xs text-[var(--text-muted)]">
                            Criada {formatRelativeTime(status.created_at)} • capacidade estimada {status.estimated_capacity} • contatos {status.total_contacts}
                          </p>
                          <div className="mt-3 grid gap-2 text-xs text-[var(--text-muted)] sm:grid-cols-2 xl:grid-cols-4">
                            <div>Enfileiradas: <span className="font-semibold text-[var(--text-primary)]">{status.total_queued}</span></div>
                            <div>Pendentes/Processando: <span className="font-semibold text-[var(--text-primary)]">{status.total_pending + status.total_reserved + status.total_sending}</span></div>
                            <div>Enviadas/Entregues: <span className="font-semibold text-[var(--text-primary)]">{status.sent_count + status.total_delivered}</span></div>
                            <div>Falhas/Bloqueadas: <span className="font-semibold text-[var(--text-primary)]">{status.failed_count + status.total_blocked + status.total_skipped + status.total_cancelled}</span></div>
                          </div>
                          {status.last_error ? (
                            <p className="mt-2 text-xs text-[var(--color-error)]">{status.last_error}</p>
                          ) : null}
                          {'instances' in status && status.instances.length > 0 ? (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {status.instances.slice(0, 4).map((instance) => (
                                <span
                                  key={instance.instance_id}
                                  className="inline-flex items-center gap-1 rounded-full border border-[var(--border-subtle)] bg-[rgba(255,255,255,0.03)] px-3 py-1 text-[11px] text-[var(--text-secondary)]"
                                >
                                  {instance.name}: {instance.status} • {instance.sent_count} envio(s)
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {canRestart ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleStartExistingCampaign(campaign.id).catch(() => undefined)}
                            >
                              Reenfileirar
                            </Button>
                          ) : null}
                          {canPause ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePauseCampaign(campaign.id).catch(() => undefined)}
                            >
                              Pausar
                            </Button>
                          ) : null}
                          {canResume ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResumeCampaign(campaign.id).catch(() => undefined)}
                            >
                              Retomar
                            </Button>
                          ) : null}
                          {(canPause || canResume) ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCancelCampaign(campaign.id).catch(() => undefined)}
                            >
                              Cancelar
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </Card>
        </div>
      </div>

      <Modal
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        title="Importar contatos da página Contatos"
        description="Os contatos selecionados são vinculados à lista atual. Os registros originais permanecem na base global."
        size="lg"
      >
        {(() => {
          const term = importSearch.trim().toLowerCase()
          const filtered = importableContacts.filter((contact) => {
            if (!term) return true
            const haystack = `${contact.name ?? ''} ${contact.push_name ?? ''} ${contact.phone_number ?? ''} ${contact.jid ?? ''}`.toLowerCase()
            return haystack.includes(term)
          })
          const allSelected = filtered.length > 0 && filtered.every((contact) => importSelection.has(contact.id))
          return (
            <div className="space-y-4">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <input
                  type="search"
                  value={importSearch}
                  onChange={(event) => setImportSearch(event.target.value)}
                  placeholder="Buscar por nome ou telefone..."
                  className="w-full rounded-[var(--radius-md)] border border-[var(--border-medium)] bg-[var(--glass-light)] py-2.5 pl-9 pr-4 text-sm text-[var(--text-primary)] outline-none transition focus:border-[var(--color-brand)] focus:ring-2 focus:ring-[rgba(var(--color-brand-rgb),0.15)]"
                />
              </div>

              <div className="flex items-center justify-between gap-3 text-xs text-[var(--text-muted)]">
                <button
                  type="button"
                  onClick={() => toggleImportAll(filtered)}
                  className="font-semibold text-[var(--color-brand)] hover:underline"
                  disabled={filtered.length === 0}
                >
                  {allSelected ? 'Desmarcar todos visíveis' : 'Selecionar todos visíveis'}
                </button>
                <span>
                  {importSelection.size} selecionado(s) · {filtered.length} de {importableContacts.length}
                </span>
              </div>

              <div className="max-h-[420px] overflow-y-auto rounded-[var(--radius-lg)] border border-[var(--border-subtle)]">
                {isLoadingImportable ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 size={18} className="animate-spin text-[var(--color-brand)]" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="px-4 py-10 text-center text-sm text-[var(--text-muted)]">
                    {importableContacts.length === 0
                      ? 'Nenhum contato cadastrado na página Contatos.'
                      : 'Nenhum contato corresponde à busca.'}
                  </div>
                ) : (
                  filtered.map((contact) => {
                    const checked = importSelection.has(contact.id)
                    return (
                      <label
                        key={contact.id}
                        className={`flex cursor-pointer items-center gap-3 border-b border-[var(--border-subtle)] px-4 py-2.5 last:border-b-0 transition hover:bg-[var(--glass-light)] ${
                          checked ? 'bg-[rgba(var(--color-brand-rgb),0.08)]' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleImportContact(contact.id)}
                          className="h-4 w-4"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-[var(--text-primary)]">
                            {contact.name || contact.push_name || contact.phone_number || contact.jid}
                          </p>
                          <p className="truncate text-xs text-[var(--text-muted)]">
                            {contact.phone_number || contact.jid}
                          </p>
                        </div>
                      </label>
                    )
                  })
                )}
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setIsImportModalOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={() => handleConfirmImport().catch(() => undefined)}
                  loading={isImporting}
                  disabled={importSelection.size === 0}
                >
                  Importar selecionados
                </Button>
              </div>
            </div>
          )
        })()}
      </Modal>
    </div>
  )
}
